import { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const PIN = "111222333444555";
const KEY = "jyotish-pin-ok";

export function usePinUnlocked() {
  const [ok, setOk] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem(KEY) === "1");
  useEffect(() => {
    const handler = (e) => { if (e.key === KEY) setOk(e.newValue === "1"); };
    window.addEventListener("storage", handler);
    const onCustom = () => setOk(localStorage.getItem(KEY) === "1");
    window.addEventListener("jyotish-pin-changed", onCustom);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("jyotish-pin-changed", onCustom);
    };
  }, []);
  return ok;
}

export function unlock() {
  localStorage.setItem(KEY, "1");
  window.dispatchEvent(new Event("jyotish-pin-changed"));
}

export function lock() {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new Event("jyotish-pin-changed"));
}

/**
 * PinGate: renders children only when PIN is entered.
 * Optionally accepts `intro` React node — shown ABOVE the pin box before unlock,
 * so pages like Dashboard can keep the header + welcome banner visible even
 * before unlock. The pin box itself is centered below.
 */
export default function PinGate({ children, intro = null, subtitle = "Enter access PIN" }) {
  const ok = usePinUnlocked();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [checking, setChecking] = useState(false);

  if (ok) return children;

  const submit = (e) => {
    e?.preventDefault?.();
    setChecking(true); setErr("");
    // Tiny debounce so wrong pins don't feel instant/brute-forceable
    setTimeout(() => {
      if (pin.trim() === PIN) {
        unlock();
      } else {
        setErr("Incorrect PIN.");
        setPin("");
      }
      setChecking(false);
    }, 250);
  };

  return (
    <>
      {intro}
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-10 relative z-10">
        <form onSubmit={submit} data-testid="pin-gate"
              className="w-full max-w-sm border border-[#2A2D35] bg-[#181A1F]/70 p-6 sm:p-8 space-y-5 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="border border-[#D4AF37]/40 p-2 text-[#D4AF37]">
              <Lock size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">Access</div>
              <div className="font-heading text-xl text-[#E6E1D6] leading-tight">{subtitle}</div>
            </div>
          </div>
          <div className="text-xs font-mono text-[#A19E95] leading-relaxed">
            This section is locked. Enter the 4-digit PIN to continue. Consultations remain open —
            you can book a session without a PIN via the <span className="text-[#D4AF37]">Consult ₹20/min</span> button.
          </div>
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, ""))}
            data-testid="pin-input"
            placeholder="••••"
            className="w-full bg-[#0F1115] border border-[#2A2D35] focus:border-[#D4AF37] outline-none
                       text-[#E6E1D6] font-mono text-center text-2xl tracking-[0.6em] py-3 rounded-sm" />
          {err && (
            <div className="text-red-400 text-xs font-mono" data-testid="pin-error">{err}</div>
          )}
          <Button type="submit" disabled={pin.length < 4 || checking}
                  data-testid="pin-submit"
                  className="w-full rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] font-heading text-base">
            {checking ? <><Loader2 size={14} className="mr-2 animate-spin" /> Checking…</>
                      : "Unlock"}
          </Button>
        </form>
      </div>
    </>
  );
}
